import { useState } from "react";
import { Check, ChevronDown, Plus, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export default function PropertySelector({
  options = [],
  activeKeys = [],
  disabledKeys = [],
  onSelect,
  onRefresh,
  testId = "property-selector",
}) {
  const [open, setOpen] = useState(false);

  const available = options.filter(
    (opt) => !activeKeys.includes(opt.key) && !disabledKeys.includes(opt.key)
  );

  const dealProps = available.filter((opt) => opt.type === "deal_property");
  const standardProps = available.filter((opt) => opt.type === "standard");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-[38px] gap-1.5 rounded-sm border-dashed border-[#cccccc] bg-white px-3 text-sm font-medium text-[#4a5b68] hover:bg-[#f5f5f5] hover:text-[#323c44]"
          data-testid={`${testId}-trigger`}
        >
          <Plus className="h-3.5 w-3.5" />
          Add filter
          {available.length > 0 && (
            <Badge
              variant="secondary"
              className="ml-0.5 rounded-sm bg-[#efefef] px-1.5 py-0 text-[11px] font-semibold text-[#4a5b68]"
            >
              {available.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <Command>
          <CommandInput
            placeholder="Search deal properties..."
            className="h-9"
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-sm text-[#4a5b68]" data-testid={`${testId}-empty`}>
              {options.length === 0
                ? "Sync your portal to see available properties."
                : "No matching properties."}
            </CommandEmpty>
            {standardProps.length > 0 && (
              <CommandGroup heading="Standard">
                {standardProps.map((opt) => (
                  <CommandItem
                    key={opt.key}
                    value={`${opt.label} ${opt.key}`}
                    onSelect={() => {
                      onSelect(opt.key);
                      setOpen(false);
                    }}
                    className="cursor-pointer gap-2"
                    data-testid={`${testId}-option-${opt.key}`}
                  >
                    <span className="truncate text-sm text-[#323c44]">{opt.label}</span>
                    <span className="ml-auto text-[11px] text-[#7d8b96]">{opt.values?.length || 0}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {dealProps.length > 0 && (
              <CommandGroup heading="Deal properties">
                {dealProps.map((opt) => (
                  <CommandItem
                    key={opt.key}
                    value={`${opt.label} ${opt.key}`}
                    onSelect={() => {
                      onSelect(opt.key);
                      setOpen(false);
                    }}
                    className="cursor-pointer gap-2"
                    data-testid={`${testId}-option-${opt.key}`}
                  >
                    <span className="truncate text-sm text-[#323c44]">{opt.label}</span>
                    <span className="ml-auto text-[11px] text-[#7d8b96]">{opt.values?.length || 0}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          {onRefresh && (
            <div className="border-t border-[#efefef] p-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-full justify-center gap-1 text-xs text-[#4a5b68] hover:text-[#323c44]"
                onClick={() => {
                  onRefresh();
                  setOpen(false);
                }}
                data-testid={`${testId}-refresh`}
              >
                <RefreshCw className="h-3 w-3" />
                Reload properties from HubSpot
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
